import { useMemo, useState, type FormEvent } from "react";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { OnboardingShell } from "../../components/onboarding/OnboardingShell.tsx";
import * as f from "../../components/onboarding/onboarding-form.styles.ts";
import type { Json } from "../../db/types.ts";
import { TOTAL_ONBOARDING_STEPS, getStepNumber } from "../../onboarding/steps.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import * as sm from "./safe-mode.styles.ts";

// ─── Backend contract ────────────────────────────────────────────────────
//
// Mirrors the strict shape validated by public.configure_safe_mode (Migration
// 0019). Currency is locked to GHS for Phase 1A. All numeric fields must be
// JSON numbers (not strings) and obey the bounds enforced server-side.
interface SafeModeConfig {
  daily_loss_limit: number;
  weekly_loss_limit: number;
  drawdown_protection_pct: number;
  cool_off_loss_count: number;
  cool_off_hours: number;
  weekly_recommended_stake_budget: number;
  currency: "GHS";
}

type PresetKey = "conservative" | "standard" | "aggressive";

interface Preset {
  key: PresetKey;
  title: string;
  caption: string;
  config: SafeModeConfig;
}

// Founder-approved preset values. Conservative is the safest default for a
// first-time user; Aggressive is still bounded well within the backend caps.
const PRESETS: readonly Preset[] = [
  {
    key: "conservative",
    title: "Conservative",
    caption: "Tightest limits. Recommended for first-time users.",
    config: {
      daily_loss_limit: 50,
      weekly_loss_limit: 200,
      drawdown_protection_pct: 20,
      cool_off_loss_count: 2,
      cool_off_hours: 48,
      weekly_recommended_stake_budget: 100,
      currency: "GHS",
    },
  },
  {
    key: "standard",
    title: "Standard",
    caption: "Balanced limits. A reasonable starting point for most users.",
    config: {
      daily_loss_limit: 100,
      weekly_loss_limit: 500,
      drawdown_protection_pct: 30,
      cool_off_loss_count: 3,
      cool_off_hours: 24,
      weekly_recommended_stake_budget: 300,
      currency: "GHS",
    },
  },
  {
    key: "aggressive",
    title: "Aggressive",
    caption: "Looser limits. Higher exposure tolerance — review carefully.",
    config: {
      daily_loss_limit: 200,
      weekly_loss_limit: 1000,
      drawdown_protection_pct: 40,
      cool_off_loss_count: 4,
      cool_off_hours: 24,
      weekly_recommended_stake_budget: 600,
      currency: "GHS",
    },
  },
] as const;

// Order matters — this drives the readable summary block.
const SUMMARY_ROWS: readonly { readonly key: keyof SafeModeConfig; readonly label: string }[] = [
  { key: "daily_loss_limit", label: "Daily loss limit" },
  { key: "weekly_loss_limit", label: "Weekly loss limit" },
  { key: "weekly_recommended_stake_budget", label: "Weekly recommended stake budget" },
  { key: "drawdown_protection_pct", label: "Drawdown protection" },
  { key: "cool_off_loss_count", label: "Cool-off after losses" },
  { key: "cool_off_hours", label: "Cool-off duration" },
  { key: "currency", label: "Currency" },
] as const;

function formatValue(
  key: keyof SafeModeConfig,
  value: SafeModeConfig[keyof SafeModeConfig],
): string {
  if (key === "currency") return String(value);
  if (key === "drawdown_protection_pct") return `${String(value)}%`;
  if (key === "cool_off_loss_count") return `${String(value)} losses`;
  if (key === "cool_off_hours") return `${String(value)} hours`;
  return `GH₵ ${String(value)}`;
}

export function SafeModePage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { refetch } = useUserProfile();

  const [selectedKey, setSelectedKey] = useState<PresetKey>("standard");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selected = useMemo<Preset>(() => {
    const found = PRESETS.find((p) => p.key === selectedKey);
    // PRESETS is exhaustive on PresetKey, so this fallback is defensive only.
    return found ?? PRESETS[0]!;
  }, [selectedKey]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    // The strict SafeModeConfig literal is structurally a JSON object but
    // TypeScript needs an explicit cast to satisfy the generated Json type's
    // index signature. The shape is still validated server-side by the RPC.
    const { error: rpcError } = await supabase.rpc("configure_safe_mode", {
      p_config: selected.config as unknown as Json,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    await refetch();
    // AuthGate sees a complete safe_mode_config on the next render and
    // computes the next unmet gate. With Step 12 in place, that gate is
    // /onboarding/platform (preferred_platform is still null at this point).
  }

  return (
    <OnboardingShell
      stepNumber={getStepNumber("safe_mode")}
      totalSteps={TOTAL_ONBOARDING_STEPS}
      title="Set your Safe Mode limits"
      description={
        "Safe Mode is required before STRIKZA can generate real-stake recommendations later. " +
        "Phase 1A remains Phantom-only — these limits protect future recommendations, not your " +
        "independent bookmaker activity. You can pick a preset now and refine the numbers later."
      }
    >
      <form style={f.formStack} onSubmit={handleSubmit} noValidate>
        {error ? <div style={f.message("error")}>{error}</div> : null}

        <div style={sm.presetGroup} role="radiogroup" aria-label="Safe Mode preset">
          {PRESETS.map((preset) => {
            const isSel = selectedKey === preset.key;
            const c = preset.config;
            return (
              <label key={preset.key} style={isSel ? sm.presetRowSelected : sm.presetRow}>
                <input
                  type="radio"
                  name="safe-mode-preset"
                  value={preset.key}
                  checked={isSel}
                  onChange={() => setSelectedKey(preset.key)}
                  style={sm.presetInput}
                />
                <span style={sm.presetBody}>
                  <span style={sm.presetTitle}>{preset.title}</span>
                  <span style={sm.presetCaption}>{preset.caption}</span>
                  <span style={sm.presetMeta}>
                    GH₵ {c.daily_loss_limit}/day · GH₵ {c.weekly_loss_limit}/week ·{" "}
                    {c.cool_off_loss_count} losses → {c.cool_off_hours}h cool-off
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        <div style={sm.summaryCard}>
          <span style={sm.summaryHeading}>Selected limits</span>
          {SUMMARY_ROWS.map((row, idx) => {
            const isLast = idx === SUMMARY_ROWS.length - 1;
            return (
              <span key={row.key} style={isLast ? sm.summaryRowLast : sm.summaryRow}>
                <span style={sm.summaryLabel}>{row.label}</span>
                <span style={sm.summaryValue}>
                  {formatValue(row.key, selected.config[row.key])}
                </span>
              </span>
            );
          })}
        </div>

        <div style={f.buttonRow}>
          <button type="submit" style={f.primaryBtn} disabled={busy}>
            {busy ? "..." : "Save and continue"}
          </button>
        </div>
      </form>
    </OnboardingShell>
  );
}
